import {ReactElement, useEffect, useState} from 'react';
import {FeaturedProjectsSection} from 'components/FeaturedProjectsSection';
import {ProjectsSection} from 'components/ProjectsSection';
import {
  FeaturedConfigItem,
  FeaturedContent,
  FeaturedProject,
  GithubAPIResponse,
  LanguagesStripData,
  ProjectsData,
  Repository
} from 'types';

const localProjectsURL = `${process.env.PUBLIC_URL}/static_configs/projects.json`;
const featuredURL = `${process.env.PUBLIC_URL}/static_configs/featured.json`;
const s3ProjectsURL = process.env.REACT_APP_PROJECTS_SNAPSHOT_URL?.trim();
const projectSnapshotURLs = s3ProjectsURL ? [s3ProjectsURL, localProjectsURL] : [localProjectsURL];

type PinnedItems = GithubAPIResponse['data']['user']['pinnedItems'];

type ProjectsState = {
  content: FeaturedContent;
  data: ProjectsData;
};

type ProjectLookups = {
  all: Map<string, Repository>;
  filtered: Map<string, Repository>;
};

async function fetchProjectsSnapshot(url: string, signal: AbortSignal): Promise<GithubAPIResponse> {
  const response = await fetch(url, {signal});

  if (!response.ok) {
    throw new Error(`${url} failed with ${response.status}`);
  }

  return response.json() as Promise<GithubAPIResponse>;
}

async function fetchProjectsSnapshotWithFallback(urls: string[], signal: AbortSignal): Promise<GithubAPIResponse> {
  let lastError: unknown = null;

  for (const url of urls) {
    try {
      return await fetchProjectsSnapshot(url, signal);
    } catch (err: unknown) {
      lastError = err;
    }
  }

  throw new Error(lastError instanceof Error ? lastError.message : String(lastError));
}

async function fetchFeaturedContent(signal: AbortSignal): Promise<FeaturedContent> {
  const response = await fetch(featuredURL, {signal});

  if (!response.ok) {
    throw new Error(`${featuredURL} failed with ${response.status}`);
  }

  return response.json() as Promise<FeaturedContent>;
}

function normalizeRepoRef(value: string): string {
  return value.trim().toLowerCase();
}

function getRepositoryLookup(repos: Repository[]): Map<string, Repository> {
  const lookup = new Map<string, Repository>();

  repos.forEach((repo) => {
    lookup.set(normalizeRepoRef(repo.id), repo);
    lookup.set(normalizeRepoRef(repo.name), repo);
    lookup.set(normalizeRepoRef(repo.nameWithOwner), repo);
  });

  return lookup;
}

function getRepoTags(repo?: Repository): string[] {
  return repo?.repositoryTopics.nodes.map((node) => node.topic.name) ?? [];
}

function getFeaturedLanguages(item: FeaturedConfigItem): LanguagesStripData | undefined {
  if (!item.languages?.length) {
    return undefined;
  }

  const total = item.languages.reduce((sum, language) => sum + (language.size ?? 1), 0);

  return item.languages.map((language) => {
    const size = language.size ?? 1;
    const width = total > 0 ? Math.round((size / total) * 100) : 0;

    return {
      id: language.id ?? language.name,
      name: language.name,
      color: language.color,
      height: `${width}%`
    };
  });
}

function isConfiguredBlogPost(item: FeaturedConfigItem): boolean {
  return Object.prototype.hasOwnProperty.call(item, 'blogUrl');
}

function getFeaturedProject(item: FeaturedConfigItem, lookup: Map<string, Repository>): FeaturedProject | null {
  const repoRef = item.repo ?? item.id;
  const repo = lookup.get(normalizeRepoRef(repoRef));
  const title = item.title ?? repo?.name;
  const description = item.description ?? repo?.description ?? undefined;
  const isBlogPost = isConfiguredBlogPost(item);

  if (!title || (!description && !repo?.readme?.text)) {
    return null;
  }

  return {
    id: item.id,
    title,
    eyebrow: item.eyebrow ?? (isBlogPost ? 'Project writeup' : 'Project'),
    description,
    blogUrl: item.blogUrl || undefined,
    codeUrl: item.codeUrl || undefined,
    repo,
    tags: item.tags ?? getRepoTags(repo),
    languages: getFeaturedLanguages(item),
    isBlogPost
  };
}

function getConfiguredProjects(featured: FeaturedContent, lookup: Map<string, Repository>): FeaturedProject[] {
  return featured.items
    .map((item) => getFeaturedProject(item, lookup))
    .filter((project): project is FeaturedProject => Boolean(project));
}

function getPinnedProjects(pinnedItems: PinnedItems, lookup: Map<string, Repository>): FeaturedProject[] {
  return pinnedItems.edges
    .map((pinned) => lookup.get(normalizeRepoRef(pinned.node.id)))
    .filter((repo): repo is Repository => Boolean(repo))
    .map((repo) => ({
      id: repo.id,
      title: repo.name,
      eyebrow: 'GitHub Repo',
      description: repo.description || undefined,
      repo,
      tags: getRepoTags(repo),
      isBlogPost: false
    }));
}

function getSelectedProjects(pinnedItems: PinnedItems, featured: FeaturedContent, lookup: Map<string, Repository>) {
  const selectedProjects = getConfiguredProjects(featured, lookup);
  const selectedIds = new Set(selectedProjects.map((project) => project.repo?.id ?? project.id));

  getPinnedProjects(pinnedItems, lookup).forEach((project) => {
    const id = project.repo?.id ?? project.id;

    if (!selectedIds.has(id)) {
      selectedIds.add(id);
      selectedProjects.push(project);
    }
  });

  return selectedProjects;
}

function getFeaturedRepoIds(featured: FeaturedContent, lookup: Map<string, Repository>): Set<string> {
  const repoIds = new Set<string>();

  featured.items.forEach((item) => {
    const repoRef = item.repo ?? item.id;
    const repo = lookup.get(normalizeRepoRef(repoRef));

    if (repo) {
      repoIds.add(repo.id);
    }
  });

  return repoIds;
}

function getPinnedRepoIds(pinnedItems: PinnedItems): Set<string> {
  return new Set(pinnedItems.edges.map((pinned) => pinned.node.id));
}

function getProjectLookups(repositories: Repository[], filteredRepos: Repository[]): ProjectLookups {
  return {
    all: getRepositoryLookup(repositories),
    filtered: getRepositoryLookup(filteredRepos)
  };
}

function getProjects({data}: GithubAPIResponse, featured: FeaturedContent): ProjectsData {
  const repositories = data.user.repositories.nodes;
  const filteredRepos = data.user.repositories.nodes.filter((repo) =>
    !repo.name.startsWith('.')
      && !repo.isFork
      && !repo.repositoryTopics.nodes.some((topicNode) => topicNode.topic.name.startsWith('submodule'))
  );
  const lookups = getProjectLookups(repositories, filteredRepos);
  const selectedProjects = getSelectedProjects(data.user.pinnedItems, featured, lookups.filtered);
  const suppressedRepoIds = new Set([
    ...Array.from(getFeaturedRepoIds(featured, lookups.all)),
    ...Array.from(getPinnedRepoIds(data.user.pinnedItems))
  ]);

  return {
    selected_projects: selectedProjects,
    other_repos: filteredRepos.filter((repo) => !suppressedRepoIds.has(repo.id))
  };
}

function ProjectsStatus({message}: { message: string }): ReactElement {
  return (
    <div className="section-inner projects-list-root">
      <div className="section-head">
        <h2 className="section-title">Projects</h2>
        <p className="section-subtitle">{message}</p>
      </div>
    </div>
  );
}

export function Projects(): ReactElement {
  const [projects, setProjects] = useState<ProjectsState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    Promise.all([
      fetchProjectsSnapshotWithFallback(projectSnapshotURLs, controller.signal),
      fetchFeaturedContent(controller.signal)
    ])
      .then(([response, content]) => {
        if (active) {
          setProjects({
            content,
            data: getProjects(response, content)
          });
        }
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }

        if (active) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  if (error) {
    return <ProjectsStatus message={`Unable to load GitHub project snapshot: ${error}`} />;
  }

  if (!projects) {
    return <ProjectsStatus message="Loading GitHub project snapshot." />;
  }

  return (
    <div className="section-inner projects-list-root">
      <div className="projects-list-section featured">
        <FeaturedProjectsSection
          title={projects.content.title}
          subtitle={projects.content.subtitle}
          projects={projects.data.selected_projects}
        />
      </div>

      <div className="projects-list-section other">
        <ProjectsSection
          title="Other projects"
          subtitle={
            <>
              Additional projects from my GitHub. See everything on{' '}
              <a href="https://github.com/bshreddy">GitHub</a>.
            </>
          }
          repos={projects.data.other_repos}
          scrollHint
        />
      </div>
    </div>
  );
}
